/**
 * The signed-in user and the organizations they can act in, read from the Go
 * backend via `GET /users/me`.
 *
 * Like `accessibleProjects.ts` this talks to the backend directly with the
 * session cookie, not through the Backstage proxy. It is the one way for the
 * frontend to learn the backend id the `/organizations/{id}/...` endpoints take
 * (the token carries the Keycloak organization id, and the session cookie is
 * HttpOnly).
 */

import { apiCall, API_BASE_URL } from '../utils/apiClient';

/**
 * The user's role in an organization. `none` is for platform staff, who hold
 * no organization grants.
 */
export type OrganizationRole = 'admin' | 'member' | 'none';

export interface Organization {
  /** The backend organization id: a positive integer, as a decimal string. */
  id: string;
  name: string;
  slug: string;
  isPlatformStaff: boolean;
  role: OrganizationRole;
  /**
   * True for the organization the session acts in. The access token is tied to
   * one organization and every organization-scoped backend operation
   * authorizes against it, so the server says which one it is.
   */
  isCurrent: boolean;
}

export interface CurrentUser {
  user: {
    /** The backend user id: a positive integer, as a decimal string. */
    id: string;
    /** May be empty: it comes from the identity provider profile. */
    name: string;
    email: string;
  };
  organizations: Organization[];
}

// The backend emits every field, so a missing one means a broken response.
interface OrganizationDto {
  id?: string; // int64 as a decimal string
  name?: string;
  slug?: string;
  isPlatformStaff?: boolean;
  role?: string;
  isCurrent?: boolean;
}

interface CurrentUserDto {
  user?: {
    id?: string;
    name?: string;
    email?: string;
  };
  organizations?: OrganizationDto[];
}

/** A positive integer id, in the decimal form protojson writes. */
function toId(value: unknown): string | null {
  if (typeof value !== 'string' || !/^[1-9][0-9]*$/.test(value)) {
    return null;
  }
  return Number.isSafeInteger(Number(value)) ? value : null;
}

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value !== '';
}

function toRole(value: string | undefined): OrganizationRole {
  switch (value) {
    case 'ORGANIZATION_ROLE_ADMIN':
      return 'admin';
    case 'ORGANIZATION_ROLE_MEMBER':
      return 'member';
    default:
      return 'none';
  }
}

function invalid(what: string): Error {
  return new Error(`The current user response has ${what}`);
}

function toOrganization(dto: OrganizationDto): Organization {
  const id = toId(dto.id);
  if (id === null) {
    throw invalid('an organization without a usable id');
  }
  if (!nonEmpty(dto.name) || !nonEmpty(dto.slug)) {
    throw invalid(`organization ${id} without a name or slug`);
  }
  return {
    id,
    name: dto.name,
    slug: dto.slug,
    isPlatformStaff: dto.isPlatformStaff === true,
    role: toRole(dto.role),
    isCurrent: dto.isCurrent === true,
  };
}

/**
 * Fetch the signed-in user and their organizations. Throws on a malformed
 * response rather than acting on part of it; a 401 is handled by `apiCall`.
 */
export async function getCurrentUser(): Promise<CurrentUser> {
  const response = await apiCall(`${API_BASE_URL}/users/me`, {
    method: 'GET',
  });
  const dto = (await response.json()) as CurrentUserDto;

  const userId = toId(dto.user?.id);
  if (userId === null || !dto.user) {
    throw invalid('no usable user id');
  }
  if (!nonEmpty(dto.user.email)) {
    throw invalid('a user without an email');
  }
  if (typeof dto.user.name !== 'string') {
    throw invalid('a user without a name field');
  }
  if (!Array.isArray(dto.organizations)) {
    throw invalid('no organization list');
  }

  return {
    user: {
      id: userId,
      name: dto.user.name,
      email: dto.user.email,
    },
    organizations: dto.organizations.map(toOrganization),
  };
}
